
import { useCallback } from 'react';
import { sendEmail, replyToEmail, saveDraft as apiSaveDraft, updateDraft, getDraft, sendDraftEmail } from '@/services/api/email';

export const useEmailCompositionOperations = () => {

  const sendEmailOperation = useCallback(async (emailData: any) => {
    try {
      // Clean the email data before sending
      const cleanEmailData = {
        from: emailData.from || '',
        from_name: emailData.from_name || '',
        to: Array.isArray(emailData.to) ? emailData.to : [emailData.to].filter(Boolean),
        cc: Array.isArray(emailData.cc) ? emailData.cc : (emailData.cc ? [emailData.cc] : []),
        bcc: Array.isArray(emailData.bcc) ? emailData.bcc : (emailData.bcc ? [emailData.bcc] : []),
        subject: emailData.subject || '',
        body: emailData.body || '',
        body_html: emailData.body_html || '',
        read: emailData.read || false,
        folder: emailData.folder || 'sent',
        thread_id: emailData.thread_id || '',
        reply_to: emailData.reply_to || '',
        // ponytail: lead context forwarded to the send endpoint so the
        // backend can log a lead_activities row + BCC the sender.
        source: emailData.source,
        lead_id: emailData.lead_id,
        lead_contact_id: emailData.lead_contact_id,
      };

      console.log('📧 Cleaned email data for API:', cleanEmailData);
      
      const response = await sendEmail(cleanEmailData);
      console.log('📧 Email sent successfully');
      return response;
    } catch (error) {
      console.error('💥 Failed to send email:', error);
      throw error;
    }
  }, []);

  const saveDraft = useCallback(async (draftData: any, draftId?: string): Promise<string> => {
    try {
      console.log('📧 Saving draft...', { draftData, draftId });
      
      // Clean the draft data
      const cleanDraftData = {
        from: draftData.from || '',
        from_name: draftData.from_name || '',
        to: Array.isArray(draftData.to) ? draftData.to : (draftData.to ? [draftData.to] : []),
        cc: Array.isArray(draftData.cc) ? draftData.cc : (draftData.cc ? [draftData.cc] : []),
        bcc: Array.isArray(draftData.bcc) ? draftData.bcc : (draftData.bcc ? [draftData.bcc] : []),
        subject: draftData.subject || '',
        body: draftData.body || '',
        body_html: draftData.body_html || '',
        thread_id: draftData.thread_id || '',
        reply_to: draftData.reply_to || ''
      };

      // Use the unified API endpoint for both create and update
      const response = await apiSaveDraft(cleanDraftData, draftId);
      
      // Return the existing draftId if updating, or the new ID from response
      return draftId || response.data.draft_id;
    } catch (error) {
      console.error('💥 Failed to save draft:', error);
      throw error;
    }
  }, []);

  const loadDraft = useCallback(async (draftId: string) => {
    try {
      console.log('📧 Loading draft:', draftId);
      const response = await getDraft(draftId);
      console.log('📧 Raw draft response:', response);
      
      // The API response has the data directly, not in a nested .data property
      const draftData = response.data || response;
      console.log('📧 Processed draft data:', draftData);
      
      return draftData;
    } catch (error) {
      console.error('💥 Failed to load draft:', error);
      throw error;
    }
  }, []);

  const sendDraft = useCallback(async (draftId: string) => {
    try {
      console.log('📧 Sending draft:', draftId);
      const response = await sendDraftEmail(draftId);
      return response;
    } catch (error) {
      console.error('💥 Failed to send draft:', error);
      throw error;
    }
  }, []);

  const replyToEmailOperation = useCallback(async (
    emailId: string,
    replyData: { body: string; body_html?: string }
  ) => {
    try {
      console.log('📧 Sending reply to email:', emailId);
      const response = await replyToEmail(emailId, replyData);
      console.log('📧 Reply sent successfully');
      return response;
    } catch (error) {
      console.error('💥 Failed to send reply:', error);
      throw error;
    }
  }, []);

  return {
    sendEmail: sendEmailOperation,
    replyToEmail: replyToEmailOperation,
    saveDraft,
    loadDraft,
    sendDraft,
  };
};
